import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/lib/constants';
import { tokenStore } from '@/lib/api';
import type { AuthTokens, AuthUser, AdminUser } from '@/types/api';

interface AuthState {
  user: AuthUser | null;
  admin: AdminUser | null;
  hydrated: boolean;

  setUser: (user: AuthUser | null, tokens?: AuthTokens | null) => void;
  setAdmin: (admin: AdminUser | null, tokens?: AuthTokens | null) => void;
  signOutUser: () => void;
  signOutAdmin: () => void;
  setHydrated: (value: boolean) => void;
}

const PERSIST_KEY = 'tally:auth:store';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      admin: null,
      hydrated: false,

      setUser: (user, tokens) => {
        if (tokens !== undefined) tokenStore.setUser(tokens);
        // Coerce undefined → null: `undefined` is dropped by JSON.stringify, so it
        // would not persist, yet `undefined !== null` would still satisfy the guard
        // in-memory — fine until reload, then it logs the session out.
        set({ user: user ?? null });
      },
      setAdmin: (admin, tokens) => {
        if (tokens !== undefined) tokenStore.setAdmin(tokens);
        set({ admin: admin ?? null });
      },
      signOutUser: () => {
        tokenStore.setUser(null);
        set({ user: null });
      },
      signOutAdmin: () => {
        tokenStore.setAdmin(null);
        set({ admin: null });
      },
      setHydrated: (value) => set({ hydrated: value }),
    }),
    {
      name: PERSIST_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ user: state.user, admin: state.admin }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    },
  ),
);

/** Helper hooks for ergonomic access in components. */
export const useUser = () => useAuthStore((s) => s.user);
export const useAdmin = () => useAuthStore((s) => s.admin);
export const useIsAuthenticated = () =>
  useAuthStore((s) => s.user !== null && tokenStore.getUser() !== null);
export const useIsAdminAuthenticated = () =>
  useAuthStore((s) => s.admin !== null && tokenStore.getAdmin() !== null);

/** Used by the storage event listener so other tabs sign in/out together. */
export function syncFromStorage() {
  if (localStorage.getItem(STORAGE_KEYS.authTokens) === null) {
    useAuthStore.getState().signOutUser();
  }
  if (localStorage.getItem(STORAGE_KEYS.adminAuthTokens) === null) {
    useAuthStore.getState().signOutAdmin();
  }
}
